import {
  emptyPvpCardSlots,
  type PvpAppearance,
  type PvpBlastFrame,
  type PvpBleedBurstFrame,
  type PvpBleedFrame,
  type PvpCardSlotFrame,
  type PvpCloseReason,
  type PvpCombatProfileWire,
  type PvpEndedFrame,
  type PvpMatchState,
  type PvpMissileFrame,
  type PvpPoisonFrame,
  type PvpProcFrame,
  type PvpProjectileFrame,
  type PvpReflectFrame,
  type PvpRuneWireKind,
  type PvpSeat,
  type PvpShipFrame,
  type PvpSummonFrame,
  type PvpWelcomeFrame,
  pvpServerFrameSchema,
} from '@kenocyber/shared';
import { pvpProcDedupKey } from './proc-dedup';
import type {
  PvpParticipantBleedBurst,
  PvpParticipantBleedFrame,
  PvpParticipantFrame,
  PvpParticipantMissileFrame,
  PvpParticipantPoisonFrame,
  PvpParticipantProjectileFrame,
  PvpParticipantReflect,
  PvpRenderEffects,
  PvpRenderRune,
} from './view';

/**
 * The duel socket, as the browser sees it.
 *
 * ## The one rule this module exists to enforce
 *
 * **The server decides; this file believes it.** There is no damage here, no
 * kill, no winner and no clock of its own. What arrives is applied whole; what
 * does not arrive is not guessed at. Every rule below is a consequence of that
 * sentence, and the server contract frozen at `d9cf24a` is treated as given -
 * nothing in this client asks for it to change.
 *
 * ## What that means concretely
 *
 * - **Sequence starts at `welcome.ack + 1`.** The applied sequence belongs to
 *   the SHIP and survives a reconnection, so a reloaded page that started
 *   counting at one would have every command it ever sent dropped as a replay -
 *   silently, and only while reconnecting, which is the hardest kind of bug to
 *   see. Re-read on every welcome: a first connection, an F5, a reconnection
 *   and a superseded generation all take the number the server just gave.
 * - **Commands are never re-sent.** Intent is level-triggered - which keys are
 *   down, where the pointer is - so a command the server missed is corrected by
 *   the next one 50 ms later rather than by a retry. That is also why "do not
 *   re-send anything the ack already covers" needs no bookkeeping here: there
 *   is no code path that could.
 * - **A state frame REPLACES the world.** Ships and projectiles are taken
 *   wholesale. A projectile the server did not return is gone, not lingering;
 *   health is what the frame says, never what a local hit thought.
 * - **A rebuild may hand back a snapshot up to a second old.** The tick can go
 *   BACKWARDS, and this client accepts it without complaint - it raises
 *   {@link PvpClientState.correction} so the renderer can drop its
 *   interpolation rather than sliding a ship across the rewind.
 * - **`state` frames may simply be missing.** A backlogged connection is
 *   skipped for them by design, so nothing here counts ticks, assumes they
 *   arrive in order, or derives elapsed time from how many were seen.
 *   `welcome`, `ended`, `error` and `closing` are the reliable ones.
 * - **A result comes from an `ended` frame and from nowhere else.** Not from a
 *   ship at zero health, not from a clock reaching zero, not from a socket
 *   closing.
 *
 * ## Transport is injected
 *
 * No `WebSocket`, no `fetch`, no `setTimeout` and no `Date.now` is reached for
 * directly, so the whole protocol is provable in plain Vitest without a server,
 * a browser socket or a real twelve-second wait.
 */

/* -------------------------------------------------------------------------- */
/*  What the caller sees                                                      */
/* -------------------------------------------------------------------------- */

export type PvpPhase =
  /** Nothing has been started. */
  | 'idle'
  /** Asking the API for a capability. */
  | 'authorising'
  /** The socket is opening, or reopening after a drop. */
  | 'connecting'
  /** Welcomed. The match's own state is in {@link PvpClientState.matchState}. */
  | 'live'
  /** An `ended` frame arrived. The result is authoritative and final. */
  | 'ended'
  /** Finished with, for a reason in {@link PvpClientState.failure}. */
  | 'closed';

/**
 * Why this client stopped.
 *
 * Deliberately wider than {@link PvpCloseReason}: the server's reasons say why
 * a SOCKET closed, and a client also has to answer "the ticket route refused
 * me" and "the grace window ran out while I was trying to get back".
 */
export type PvpClientFailure =
  | PvpCloseReason
  /** The ticket route refused. The caller reads `failureDetail` for its reason. */
  | 'not_authorised'
  /** The socket dropped and did not come back inside the grace window. */
  | 'reconnect_failed'
  /** The server is running balance numbers this bundle does not have. */
  | 'rules_version_mismatch';

/** The arena, exactly as the room described it. Never a local constant. */
export interface PvpArena {
  readonly width: number;
  readonly height: number;
  readonly obstacles: ReadonlyArray<{
    readonly x: number;
    readonly y: number;
    readonly w: number;
    readonly h: number;
  }>;
  readonly shipRadius: number;
  readonly projectileRadius: number;
  /**
   * What each seat's ship looks like, as the SERVER decided.
   *
   * A seat is `null` until it has connected; the room re-sends this same
   * welcome when the second player arrives, so a client that opened first
   * learns its opponent's appearance without asking for it. The scene draws
   * from this and never from the local player's own inventory - it has no idea
   * what the other account owns, and guessing would be worse than not knowing.
   */
  /*
   * Keyed by PARTICIPANT ID, which the welcome's own `{a, b}` already satisfies
   * - in a two-seat room the ids are 'a' and 'b'. Written as a lookup rather
   * than as that pair for the same reason the ships are a list of participants:
   * `pvpWorldArena` copies this straight through to the renderer, and the
   * renderer stopped assuming there were two of them.
   */
  readonly appearance: Readonly<Record<string, PvpAppearance | null | undefined>>;
  /**
   * **O nickname de cada cadeira** (gate V28H.R), pela mesma chave.
   *
   * Do `welcome` e não do quadro: ele é estático dentro de uma partida. Ausente
   * ou `null` é uma nave sem etiqueta, que é um estado real - uma conta pode
   * não ter nome, e uma cadeira pode ainda não ter sido ocupada.
   */
  readonly nickname: Readonly<Record<string, string | null | undefined>>;
  /**
   * What each hull IS, as the ROOM decided, keyed by participant id.
   *
   * Four integers per seat - base damage, maximum health, speed and cadence -
   * and the client's only source for any of them. It used to read
   * `PVP_BASE_SPEED_BPS` and `arena.maxHealth` out of its own bundle, which is
   * fine exactly while every hull is identical and silently wrong the moment
   * one is not.
   *
   * Never null, unlike the appearance beside it: a hull exists from the moment
   * the room builds its simulation, so there is no instant where the server
   * does not know one and no reason for a client-side default.
   */
  readonly combatProfile: Readonly<Record<string, PvpCombatProfileWire | undefined>>;
  /**
   * A referência NORMALIZADA por que a métrica do casco divide, em MILLI.
   *
   * `PVP_MAX_HEALTH_MILLI`, e é por ela que `pvpVisualScale` divide para
   * desenhar um casco maior.
   *
   * NÃO é o denominador de uma barra de vida. Esse é
   * `combatProfile[participante].maxHealthMilli`, que é outra pergunta.
   */
  readonly maxHealthMilli: number;
  readonly tickMs: number;
  readonly timeLimitMs: number;
  readonly reconnectGraceMs: number;
}

export interface PvpClientState {
  readonly phase: PvpPhase;
  /**
   * Which SEAT this browser is. Never a list to choose from.
   *
   * Deliberately still `PvpSeat` while `ships` below has been widened to
   * participants, and the asymmetry is the point: this field is what the HUD
   * and `pvpResultText` compare against `ended.winner`, and a winner is a seat
   * because the room has two of them. Generalising it would generalise the
   * RESULT, which is a rule rather than a picture.
   *
   * The renderer does not read it. `PvpScene` learns which participant this
   * browser is from `pvp:open`, which is where the widening stops.
   */
  readonly seat: PvpSeat | null;
  readonly matchId: string | null;
  readonly arena: PvpArena | null;
  /** The match's own lifecycle state, as the server last reported it. */
  readonly matchState: PvpMatchState | null;
  /** The authoritative tick. It can go backwards after a rebuild. */
  readonly tick: number;
  /**
   * The ships, as PARTICIPANTS rather than as two seats.
   *
   * A widening of {@link PvpShipFrame} and nothing more: the socket path
   * assigns its parsed frames here unchanged, because a `'a' | 'b'` seat is a
   * participant id and the extra `team` is optional. What it buys is that the
   * renderer below can be handed a roster the wire has no word for yet -
   * a fixture, a replay - without either side casting.
   */
  readonly ships: readonly PvpParticipantFrame[];
  /** Exactly what the last state frame carried. Nothing is kept alive locally. */
  readonly projectiles: readonly PvpParticipantProjectileFrame[];
  /** Os mísseis em voo (gate V28G.L). Uma lista à parte, como no fio. */
  readonly missiles: readonly PvpParticipantMissileFrame[];
  /**
   * As invocações a existir (gate V28H.L). **Opcional**, e é deliberado.
   *
   * Ausente quer dizer NENHUMA, que é o estado de quase toda a partida - e é
   * também o estado de um cliente que nunca viu uma. Obrigá-la a existir
   * forçaria dezenas de fixtures a escrever uma lista vazia para dizer o que a
   * ausência já diz.
   */
  readonly summons?: readonly PvpSummonFrame[] | undefined;
  /**
   * Os stacks de veneno ATIVOS agora (gate V28G.M).
   *
   * ESTADO, e não "o que houve desde o frame anterior" como os procs e as
   * explosões são: um stack dura seis segundos e tem uma marca desenhada
   * durante esse tempo. Tomada por inteiro, como tudo o resto - um stack que o
   * servidor deixou de mandar acabou, e mantê-lo vivo localmente seria este
   * cliente a desenhar veneno que a sala já não aplica.
   */
  readonly poison: readonly PvpParticipantPoisonFrame[];
  /**
   * As marcas de sangramento ATIVAS agora (gate V28G.N).
   *
   * ESTADO, como os stacks de veneno, e pela mesma razão: uma marca existe até
   * alguém a mandar rebentar. Tomada por inteiro - uma marca que o servidor
   * deixou de mandar acabou, e mantê-la viva localmente seria este cliente a
   * desenhar uma ameaça que a sala já não tem.
   *
   * Traz a AUTORIA, ao contrário de um stack: é ela que decide quantas marcas o
   * botão deste jogador conta, e quais o efeito anima.
   */
  readonly bleeding: readonly PvpParticipantBleedFrame[];
  /**
   * What each seat is under the influence of, as the room counts it.
   *
   * Taken whole, like everything else. A client that ran its own timers would
   * keep an effect alive past the tick the room ended it on, and for Double
   * Damage that is the difference between a shot the player expects to land
   * for eighty and one that lands for forty.
   */
  readonly effects: readonly PvpRenderEffects[];
  /**
   * **Os três espaços de cartas DESTA cadeira** (gate V28H.J).
   *
   * Da metade privada do frame, e por isso só os desta ligação: um arrefecimento
   * é informação de decisão, e o que é público - os canhões estão montados -
   * chega por outro caminho, no casco de cada nave.
   *
   * Tomados inteiros, como tudo o resto: um cliente que corresse os seus
   * próprios relógios mostraria uma carta pronta um tique antes de ela estar.
   */
  readonly cards: readonly PvpCardSlotFrame[];
  /** Every pad and what is on it. Where a pad IS comes from a shared constant. */
  readonly runes: readonly PvpRenderRune[];
  readonly msLeft: number;
  /** The highest command sequence the server has applied for this seat. */
  readonly ack: number;
  /**
   * Bumped whenever the authoritative world was replaced discontinuously - a
   * welcome, or a tick that moved backwards. A renderer must not interpolate
   * across a change in this number.
   */
  readonly correction: number;
  /** Only ever set by an `ended` frame. */
  readonly result: PvpEndedFrame | null;
  readonly failure: PvpClientFailure | null;
  /** The server's machine reason behind a failure, when there was one. */
  readonly failureDetail: string | null;
}

/** What a player may say. There is no position, no health and no damage here. */
export interface PvpIntent {
  /** WASD reduced to two axes. Not a speed - the room owns speed. */
  readonly mx: -1 | 0 | 1;
  readonly my: -1 | 0 | 1;
  /** Where the pointer is, relative to the ship. Direction only. */
  readonly ax: number;
  readonly ay: number;
  readonly firing: boolean;
  /**
   * Where the player clicked, for THIS frame only.
   *
   * A REQUEST and never a result. The room validates the point against the
   * arena, the structures, the hull's radius, the charge and the ship's life,
   * and answers with a position IT chose; a refusal moves nothing and spends
   * nothing. What no field here can say is where the ship ENDED UP.
   */
  readonly teleportTo?: { readonly x: number; readonly y: number } | null;
  /**
   * A ordem de DETONAR as marcas, para ESTE frame apenas (gate V28G.N).
   *
   * Uma INTENÇÃO e nunca um resultado. Não diz alvo, contagem nem dano - não há
   * campo onde isso caiba, aqui nem no fio - e a sala decide sozinha o que
   * rebenta a partir de um estado que este cliente não escreve. Sem marcas, é um
   * `no-op` seguro.
   */
  readonly detonateBleed?: boolean;
  /**
   * A ORDEM de ligar uma carta, para ESTE frame apenas (gate V28H.J).
   *
   * Uma INTENÇÃO e nunca um resultado, como a detonação. Diz o espaço e a
   * identidade da ordem, e mais nada: não há campo para a carta, a duração, o
   * arrefecimento nem outro participante - a sala decide tudo isso a partir do
   * loadout que ela própria congelou.
   */
  readonly activateCard?: { readonly slot: 1 | 2 | 3; readonly commandId: string } | null;
  /** A ordem de PILOTAR o que este casco está a tocar (gate V28H.X). */
  readonly boardSummon?: { readonly commandId: string } | null;
  /** A ordem de SOLTAR uma carga (gate V28H.N). */
  readonly releaseCard?: { readonly slot: 1 | 2 | 3; readonly commandId: string } | null;
}

/* -------------------------------------------------------------------------- */
/*  Transport, injected                                                       */
/* -------------------------------------------------------------------------- */

/**
 * The slice of a WebSocket this client uses.
 *
 * Four callbacks and two methods. A test supplies a fake; the browser supplies
 * {@link browserPvpSocket}. Nothing here knows about `WebSocket`, so nothing
 * here needs a browser to be proven.
 */
export interface PvpSocket {
  send(data: string): void;
  close(): void;
  onOpen(handler: () => void): void;
  onMessage(handler: (data: string) => void): void;
  onClose(handler: () => void): void;
  onError(handler: () => void): void;
}

export interface PvpConnectionDeps {
  /** Mints a ONE-USE capability. Called again for every reconnection. */
  readonly mintTicket: (matchId: string) => Promise<{ ticket: string }>;
  readonly socketUrl: (ticket: string) => string;
  readonly openSocket: (url: string) => PvpSocket;
  readonly now: () => number;
  /** Injected so a test can move twelve seconds without waiting for them. */
  readonly schedule: (run: () => void, ms: number) => () => void;
  /** The rules version this bundle was built against. */
  readonly rulesVersion: number;
}

/** How long to wait before trying the socket again after an unexpected drop. */
const RECONNECT_DELAY_MS = 500;

const EMPTY: PvpClientState = {
  phase: 'idle',
  seat: null,
  matchId: null,
  arena: null,
  matchState: null,
  tick: 0,
  ships: [],
  projectiles: [],
  missiles: [],
  effects: [],
  /* Três espaços vazios, que é o estado antes de qualquer frame. */
  cards: emptyPvpCardSlots(),
  runes: [],
  poison: [],
  bleeding: [],
  msLeft: 0,
  ack: 0,
  correction: 0,
  result: null,
  failure: null,
  failureDetail: null,
};

/* -------------------------------------------------------------------------- */

/** Partilhada e congelada: uma drenagem vazia nao aloca. */
const NO_PROCS: readonly PvpProcFrame[] = Object.freeze([]);

/** A gémea das explosões (gate V28G.J). */
const NO_BLASTS: readonly PvpBlastFrame[] = Object.freeze([]);

/** E uma drenagem vazia de detonações também não aloca (gate V28G.N). */
const NO_BURSTS: readonly PvpParticipantBleedBurst[] = Object.freeze([]);

/** Partilhada e congelada: um quadro sem reflexões não aloca (gate V28G.O). */
const NO_REFLECTS: readonly PvpParticipantReflect[] = Object.freeze([]);

export class PvpConnection {
  private state: PvpClientState = EMPTY;
  private readonly listeners = new Set<(state: PvpClientState) => void>();

  private socket: PvpSocket | null = null;
  /**
   * Which attempt owns the socket.
   *
   * Every `connect` bumps it, and every handler captures the value it was
   * created under. A frame, a close or a resolved ticket belonging to a
   * replaced attempt is DROPPED - the same rule the server applies to a
   * superseded seat, applied on this side so a slow ticket request cannot
   * seat a socket the caller has already moved on from.
   */
  private generation = 0;
  private nextSeq = 1;
  /** Set the moment the socket drops, so the grace window is measured honestly. */
  private droppedAt: number | null = null;
  private cancelRetry: (() => void) | null = null;
  private readyDeclared = false;

  constructor(private readonly deps: PvpConnectionDeps) {}

  get snapshot(): PvpClientState {
    return this.state;
  }

  subscribe(listener: (state: PvpClientState) => void): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Opens a duel socket for a match this account already holds a seat in. */
  connect(matchId: string): void {
    this.generation += 1;
    this.readyDeclared = false;
    this.droppedAt = null;
    /*
     * A memória de deduplicação MORRE com a partida, e é estrutural.
     *
     * `i` é o id do projétil, e o espaço de ids reinicia em 1 a cada sala nova.
     * Uma memória que sobrevivesse a uma partida faria a seguinte descartar os
     * seus primeiros cortes por os julgar repetidos - e o controlador criar uma
     * ligação por partida seria uma garantia do CHAMADOR, não desta classe.
     * Aqui, chamar `connect` outra vez é a definição de "outro espaço de ids".
     */
    this.seenProcs.clear();
    this.pendingProcs = [];
    this.seenBlasts.clear();
    this.pendingBlasts = [];
    this.teardownSocket();
    this.patch({
      phase: 'authorising',
      matchId,
      failure: null,
      failureDetail: null,
      result: null,
    });
    void this.authoriseAndOpen(this.generation, matchId);
  }

  /**
   * Says this client has finished loading and is ready to fight.
   *
   * Separate from the first command so a browser still building textures cannot
   * start a countdown it will spend standing still. Repeated safely: the room
   * only needs to hear it once, and a reconnecting client says it again because
   * the room has no way to know the tab is the same one.
   */
  declareReady(): void {
    this.readyDeclared = true;
    this.transmit({ t: 'ready' });
  }

  /**
   * Sends the player's current intent.
   *
   * One command, one sequence number, no retry and no queue. If the socket is
   * not open the intent is DROPPED rather than buffered: a buffer would deliver
   * a burst of stale directions the moment a connection came back, and the
   * player is holding whatever they are holding right now.
   */
  sendIntent(intent: PvpIntent): number | null {
    if (this.state.phase !== 'live' || this.state.matchState !== 'running') {
      // Dropped, and it consumes no sequence. The scene is told nothing, which
      // is what keeps its prediction from replaying a command the room will
      // never see.
      return null;
    }
    const seq = this.nextSeq;
    this.nextSeq += 1;
    this.transmit({
      t: 'cmd',
      seq,
      mx: intent.mx,
      my: intent.my,
      ax: Math.trunc(intent.ax),
      ay: Math.trunc(intent.ay),
      f: intent.firing,
      ...(intent.teleportTo
        ? { tp: { x: Math.trunc(intent.teleportTo.x), y: Math.trunc(intent.teleportTo.y) } }
        : {}),
      /*
       * A ordem de DETONAR, e só quando ela existe (gate V28G.N).
       *
       * Ausente na esmagadora maioria dos frames, como `tp` é: um campo escrito a
       * `false` vinte vezes por segundo seria vinte bytes por segundo a dizer que
       * o jogador não fez nada.
       */
      ...(intent.detonateBleed === true ? { bd: true as const } : {}),
      /*
       * E a ORDEM da carta, também só quando ela existe (gate V28H.J).
       *
       * Ausente na esmagadora maioria dos frames, pela mesma razão: um campo
       * escrito vinte vezes por segundo para dizer que ninguém carregou em nada
       * seria protocolo a pagar por silêncio.
       */
      ...(intent.activateCard
        ? { ck: { s: intent.activateCard.slot, id: intent.activateCard.commandId } }
        : {}),
      /* E a soltura, no mesmo frame e com a mesma forma (gate V28H.N). */
      ...(intent.releaseCard
        ? { cr: { s: intent.releaseCard.slot, id: intent.releaseCard.commandId } }
        : {}),
      /*
       * E a ordem de PILOTAR (gate V28H.X), que não nomeia nada: ela diz
       * "embarca no que eu estou a tocar", e a sala decide o quê.
       */
      ...(intent.boardSummon ? { bo: { id: intent.boardSummon.commandId } } : {}),
    });
    return seq;
  }

  /**
   * Tells the room the player has chosen to abandon this match (ADR 0043).
   *
   * ## Why this exists, and what it replaces
   *
   * Nothing. Before it, pressing SAIR dropped the socket and returned to the
   * hunt, so what the room saw was a disconnection - indistinguishable from an
   * F5. A duel survived that by accident, because the opponent's socket kept the
   * room ticking until the grace expired. A 3x3 where everybody pressed the
   * button did not: with no socket left, nothing drove the room's loop, and the
   * match stayed `running` with six active participants.
   *
   * ## What it does NOT do
   *
   * Tear anything down. The socket stays open precisely so the room's answer can
   * arrive on it: the caller waits for the authoritative end rather than
   * assuming one. Answering `false` means the intent could not be sent at all,
   * which the caller has to show as a failure rather than hide as an exit.
   */
  requestLeave(): boolean {
    if (this.state.phase !== 'live') {
      return false;
    }
    if (this.state.matchState !== 'running' && this.state.matchState !== 'ready') {
      return false;
    }
    return this.transmit({ t: 'leave' });
  }

  /** Lets go for good. Not a disconnection the room should wait out. */
  dispose(): void {
    this.generation += 1;
    this.cancelRetry?.();
    this.cancelRetry = null;
    this.teardownSocket();
  }

  /* ---------------------------------------------------------------------- */

  private async authoriseAndOpen(generation: number, matchId: string): Promise<void> {
    let ticket: string;
    try {
      // A FRESH capability every time. The one from last connection is spent,
      // and presenting it again is refused by the room by design.
      ticket = (await this.deps.mintTicket(matchId)).ticket;
    } catch (error) {
      if (generation !== this.generation) {
        return;
      }
      this.fail('not_authorised', reasonOf(error));
      return;
    }
    if (generation !== this.generation) {
      // The caller moved on while the request was in flight. The capability
      // simply expires unused; nothing is opened on its behalf.
      return;
    }

    this.patch({ phase: 'connecting' });
    const socket = this.deps.openSocket(this.deps.socketUrl(ticket));
    this.socket = socket;

    socket.onOpen(() => {
      if (generation !== this.generation) {
        return;
      }
      // Nothing is sent here. The room speaks first, with a welcome, and the
      // sequence this client may use comes out of it.
      this.droppedAt = null;
    });
    socket.onMessage((data) => {
      if (generation !== this.generation) {
        return;
      }
      this.receive(data);
    });
    socket.onError(() => {
      if (generation !== this.generation) {
        return;
      }
      this.handleDrop(generation);
    });
    socket.onClose(() => {
      if (generation !== this.generation) {
        return;
      }
      this.handleDrop(generation);
    });
  }

  /**
   * Reads one frame, or ignores it.
   *
   * Parsed against the shared schema rather than trusted: a frame this build
   * does not understand is dropped silently, because the alternative is a
   * renderer reading a field that is not there. Nothing thrown from here can
   * reach the socket callback.
   */
  private receive(data: string): void {
    let parsed: unknown;
    try {
      parsed = JSON.parse(data);
    } catch {
      return;
    }
    const frame = pvpServerFrameSchema.safeParse(parsed);
    if (!frame.success) {
      return;
    }

    switch (frame.data.t) {
      case 'welcome':
        this.applyWelcome(frame.data);
        return;
      case 'state':
        this.applyState(frame.data);
        return;
      case 'ended':
        /*
         * The ONLY place a result is ever set.
         *
         * Not a ship at zero health, not the clock, not a closing socket. A
         * client that announced its own result would announce a different one
         * from the server's the first time a frame went missing.
         */
        this.patch({
          phase: 'ended',
          matchState: frame.data.state,
          result: frame.data,
        });
        return;
      case 'error':
        this.patch({ failureDetail: frame.data.reason });
        return;
      case 'closing':
        this.applyClosing(frame.data.reason);
        return;
    }
  }

  private applyWelcome(welcome: PvpWelcomeFrame): void {
    if (welcome.rulesVersion !== this.deps.rulesVersion) {
      /*
       * The server is running numbers this bundle does not have.
       *
       * Refused rather than rendered. A duel drawn with one set of speeds and
       * decided with another is a duel the losing player cannot interpret, and
       * the honest answer is to tell them to reload rather than to show them a
       * fight they are not really in.
       */
      this.fail('rules_version_mismatch', `server=${welcome.rulesVersion}`);
      return;
    }

    /*
     * The next sequence, taken from the server on EVERY welcome.
     *
     * First connection, F5, reconnection, a generation replaced by another tab:
     * all four arrive here, and all four resume from what the ship has actually
     * applied. Never from one, and never from whatever this object counted to
     * before.
     */
    this.nextSeq = welcome.ack + 1;
    this.droppedAt = null;

    this.patch({
      phase: 'live',
      seat: welcome.seat,
      matchId: welcome.matchId,
      matchState: welcome.state,
      ack: welcome.ack,
      arena: {
        width: welcome.arena.w,
        height: welcome.arena.h,
        obstacles: welcome.arena.obstacles,
        shipRadius: welcome.shipRadius,
        projectileRadius: welcome.projectileRadius,
        appearance: welcome.appearance,
        /* E os nomes, da mesma mensagem (gate V28H.R). */
        nickname: welcome.nick,
        combatProfile: welcome.combatProfile,
        maxHealthMilli: welcome.maxHealthMilli,
        tickMs: welcome.tickMs,
        timeLimitMs: welcome.timeLimitMs,
        reconnectGraceMs: welcome.reconnectGraceMs,
      },
      // A welcome is a discontinuity by definition: the world it describes may
      // be a second behind the one on screen, or a whole match ahead of it.
      correction: this.state.correction + 1,
      failure: null,
    });

    if (this.readyDeclared) {
      // A reconnecting client says it again. The room cannot know this tab is
      // the same one, and a `ready` it already has costs it a boolean.
      this.transmit({ t: 'ready' });
    }
  }

  /**
   * Os procs confirmados que ainda ninguém consumiu.
   *
   * ## Uma FILA drenável, e não um campo do frame de render
   *
   * `PvpRenderFrame` é uma mistura de duas instantâneas, e é reconstruída a cada
   * quadro desenhado. Um proc que vivesse lá seria redesenhado sessenta vezes
   * por segundo enquanto as duas instantâneas não mudassem - o mesmo corte, em
   * loop. Um corte é um ACONTECIMENTO: acontece uma vez, e quem o desenha
   * consome-o.
   *
   * É a mesma forma que a caça já usa (`HuntSimulation.drainEvents`), o que faz
   * com que uma futura apresentação tenha um só padrão a seguir nos dois modos.
   */
  private pendingProcs: PvpProcFrame[] = [];

  /**
   * Os procs já aceites, para nenhum ser desenhado duas vezes.
   *
   * Um frame repetido, uma reconstrução da sala que reenvia um tique, ou um
   * `stateFor` fora da cadência trazem o mesmo proc. O primeiro conta; os outros
   * não. A chave nasce do id do PROJÉTIL e não de um contador: ele sobrevive a
   * uma hibernação da sala, e um contador reiniciado faria o cliente descartar
   * procs novos por os julgar antigos.
   *
   * ## A chave é `(projétil, EFEITO, ALVO)`, e as três partes ganharam-se
   *
   * O **projétil** sozinho não chega porque um projétil confirma mais do que um
   * facto: um tiro com o Ciclone que acerta num casco com o Orbe Vampírico
   * anuncia um corte E um sifão com o mesmo `i`, e uma chave que fosse só o id
   * deixava cair o segundo em silêncio - qualquer que ele fosse. O Coração Vital
   * tornou-o inevitável em vez de ocasional: a cura é anunciada no DISPARO, com
   * o id do projétil que a salva acabou de criar, e esse mesmo projétil ainda vai
   * poder cortar e sifonar quando aterrar.
   *
   * O **efeito** ao lado dele também não chega, e a razão é a simétrica: o mesmo
   * efeito do mesmo projétil pode acontecer sobre alvos DIFERENTES. Um payload de
   * área é a forma óbvia disso, e a fronteira certa para o dizer é esta - a
   * ligação recebe factos autoritativos e não sabe que forma o emissor tinha.
   * Uma chave sem o alvo obrigaria cada emissor futuro a lembrar-se de que a
   * apresentação o deixaria cair, e o que se esquece é sempre o que ninguém está
   * a olhar.
   *
   * O **alvo** fecha-a. O trio é único por construção: um efeito é confirmado no
   * máximo uma vez por projétil e por alvo, porque um projétil resolve contra
   * cada casco uma vez. Tudo o que a chave recusa a partir daqui é uma
   * REPETIÇÃO - um frame reenviado, um tique re-simulado - que é exatamente o
   * que ela existe para recusar.
   *
   * ## Porque não um id de evento
   *
   * Porque não existe nenhum. O canal do proc carrega o que a autoridade
   * decidiu - efeito, autor, alvo, salva, tique e magnitude - e o trio é a
   * identidade mínima que esses campos já formam. Inventar um contador seria
   * acrescentar estado ao fio para dizer o que ele já diz, e um contador
   * reiniciado por uma hibernação da sala é precisamente o defeito que o id do
   * projétil evita.
   */
  private readonly seenProcs = new Set<string>();

  /** A fila e a memória das explosões. As gémeas exatas das de cima. */
  private pendingBlasts: PvpBlastFrame[] = [];

  private readonly seenBlasts = new Set<number>();

  private acceptBlasts(blasts: readonly PvpBlastFrame[]): void {
    for (const blast of blasts) {
      if (this.seenBlasts.has(blast.i)) {
        continue;
      }
      /*
       * A chave é o id do PROJÉTIL, como a dos procs.
       *
       * Um projétil detona uma vez - é destruído por isso - portanto o id é
       * único por explosão, sobrevive a uma hibernação da sala, e um frame
       * repetido nunca desenha a mesma bomba duas vezes.
       */
      this.seenBlasts.add(blast.i);
      this.pendingBlasts.push(blast);
    }
  }

  /** Entrega as explosões confirmadas desde a última chamada, e esvazia a fila. */
  drainBlasts(): readonly PvpBlastFrame[] {
    if (this.pendingBlasts.length === 0) {
      return NO_BLASTS;
    }
    const taken = this.pendingBlasts;
    this.pendingBlasts = [];
    return taken;
  }

  /**
   * A fila e a memória das DETONAÇÕES (gate V28G.N).
   *
   * As gémeas das dos procs e das explosões, com outra chave: uma detonação não
   * tem projétil - a marca sobreviveu ao tiro que a semeou - portanto a
   * identidade é `(autor, tique)`. Um autor detona no máximo uma vez por tique,
   * por construção, e um frame repetido nunca desenha o mesmo corte duas vezes.
   */
  private pendingBursts: PvpParticipantBleedBurst[] = [];

  private readonly seenBursts = new Set<string>();

  private acceptBursts(bursts: readonly PvpBleedBurstFrame[]): void {
    for (const burst of bursts) {
      const key = `${burst.p}:${burst.t}`;
      if (this.seenBursts.has(key)) {
        continue;
      }
      this.seenBursts.add(key);
      for (const target of burst.q) {
        this.pendingBursts.push({
          key: `${key}:${target.s}`,
          p: burst.p,
          q: target.s,
          n: target.n,
          d: BigInt(target.d),
        });
      }
    }
  }

  /** Entrega as detonações confirmadas desde a última chamada, e esvazia a fila. */
  drainBleedBursts(): readonly PvpParticipantBleedBurst[] {
    if (this.pendingBursts.length === 0) {
      return NO_BURSTS;
    }
    const taken = this.pendingBursts;
    this.pendingBursts = [];
    return taken;
  }

  /**
   * A fila e a memória das REFLEXÕES (gate V28G.O).
   *
   * As gémeas das três de cima, com a sua própria chave: uma reflexão não tem
   * projétil - uma parcela de veneno intercetada não veio em bala nenhuma - e
   * não basta `(autor, tique)`, porque no mesmo tique um casco pode intercetar
   * um tiro e uma parcela do mesmo adversário. A identidade é
   * `(refletor, destinatário, tique, payload, estágio)`, que é exatamente o
   * conjunto de coisas que a distinguem.
   */
  private pendingReflects: PvpParticipantReflect[] = [];

  private readonly seenReflects = new Set<string>();

  private acceptReflects(reflects: readonly PvpReflectFrame[]): void {
    for (const reflect of reflects) {
      const key = `${reflect.p}:${reflect.q}:${reflect.t}:${reflect.k}:${reflect.s}`;
      if (this.seenReflects.has(key)) {
        continue;
      }
      this.seenReflects.add(key);
      this.pendingReflects.push({
        key,
        p: reflect.p,
        q: reflect.q,
        k: reflect.k,
        s: reflect.s,
        x: reflect.x,
        y: reflect.y,
        tx: reflect.tx,
        ty: reflect.ty,
        d: reflect.d === undefined ? null : BigInt(reflect.d),
      });
    }
  }

  /** Entrega as reflexões confirmadas desde a última chamada, e esvazia a fila. */
  drainReflects(): readonly PvpParticipantReflect[] {
    if (this.pendingReflects.length === 0) {
      return NO_REFLECTS;
    }
    const taken = this.pendingReflects;
    this.pendingReflects = [];
    return taken;
  }

  private acceptProcs(procs: readonly PvpProcFrame[]): void {
    for (const proc of procs) {
      const key = pvpProcDedupKey(proc);
      if (this.seenProcs.has(key)) {
        continue;
      }
      this.seenProcs.add(key);
      this.pendingProcs.push(proc);
    }
  }

  /**
   * Entrega os cortes confirmados desde a última chamada, e esvazia a fila.
   *
   * Nada neste ficheiro os desenha, e é deliberado: este gate transporta o
   * facto e para aí. Um consumidor que não exista ainda não pode desenhar um
   * corte a mais.
   */
  drainProcs(): readonly PvpProcFrame[] {
    if (this.pendingProcs.length === 0) {
      return NO_PROCS;
    }
    const taken = this.pendingProcs;
    this.pendingProcs = [];
    return taken;
  }

  private applyState(frame: {
    k: number;
    state: PvpMatchState;
    ack: number;
    /** Os três espaços desta cadeira (gate V28H.J). Metade privada. */
    ck: readonly PvpCardSlotFrame[];
    ships: readonly PvpShipFrame[];
    proj: readonly PvpProjectileFrame[];
    /** Os mísseis em voo (gate V28G.L). Obrigatória desde o protocolo 3. */
    mis: readonly PvpMissileFrame[];
    /** As invocações a existir (gate V28H.L). Ausente = nenhuma. */
    sm?: readonly PvpSummonFrame[] | undefined;
    /** Os stacks de veneno (gate V28G.M). Obrigatória desde o protocolo 4. */
    psn: readonly PvpPoisonFrame[];
    /** As marcas de sangramento (gate V28G.N). Obrigatória desde o protocolo 5. */
    bld: readonly PvpBleedFrame[];
    /** As detonações confirmadas (gate V28G.N). Obrigatória desde o protocolo 5. */
    bdt: readonly PvpBleedBurstFrame[];
    /** As reflexões confirmadas (gate V28G.O). Obrigatória desde o protocolo 7. */
    rfl: readonly PvpReflectFrame[];
    fx: readonly {
      s: PvpSeat;
      e: {
        dd: number;
        sp: number;
        tp: number;
      };
    }[];
    runes: readonly { p: number; k: PvpRuneWireKind | null }[];
    procs?: readonly PvpProcFrame[] | undefined;
    blasts?: readonly PvpBlastFrame[] | undefined;
    left: number;
  }): void {
    /*
     * A tick that went backwards is a REBUILD, not an error.
     *
     * The room persists a running duel about once a second, so a rebuilt object
     * resumes the last durable tick and re-simulates from there. Up to a second
     * of the fight is replayed. The client's job is to accept it and to tell
     * the renderer not to slide anything across the seam.
     */
    const rewound = frame.k < this.state.tick;
    this.acceptProcs(frame.procs ?? []);
    this.acceptBlasts(frame.blasts ?? []);
    this.acceptBursts(frame.bdt);
    this.acceptReflects(frame.rfl);
    this.patch({
      matchState: frame.state,
      tick: frame.k,
      ack: frame.ack,
      // Wholesale, both of them. A projectile the server did not send is gone,
      // and merging would be this client inventing one.
      ships: frame.ships,
      projectiles: frame.proj,
      /* Wholesale também: um míssil que o servidor deixou de mandar desapareceu. */
      missiles: frame.mis,
      /* E as invocações pela mesma regra: uma que sumiu do frame acabou. */
      summons: frame.sm ?? [],
      /* E os stacks pela mesma regra: um que sumiu do frame expirou ou morreu. */
      poison: frame.psn,
      /* E as marcas pela mesma regra: uma que sumiu do frame rebentou ou morreu. */
      bleeding: frame.bld,
      /* Os três espaços desta cadeira, tomados inteiros (gate V28H.J). */
      cards: frame.ck,
      effects: frame.fx.map((entry) => ({
        seat: entry.s,
        doubleDamageTicks: entry.e.dd,
        speedTicks: entry.e.sp,
        teleportCharges: entry.e.tp,
      })),
      runes: frame.runes.map((rune) => ({ pad: rune.p, kind: rune.k })),
      msLeft: frame.left,
      correction: rewound ? this.state.correction + 1 : this.state.correction,
    });
  }

  private applyClosing(reason: PvpCloseReason): void {
    if (reason === 'superseded') {
      /*
       * Another tab took this seat. NOT a reconnection to attempt.
       *
       * The player is still there - they just moved tab - and a client that
       * fought its way back would produce two tabs taking the seat from each
       * other for as long as both are open.
       */
      this.fail('superseded', null);
      return;
    }
    if (reason === 'match_over') {
      // The room is letting go. If the ending already arrived, keep it: a
      // socket closing must never overwrite the result it delivered.
      this.generation += 1;
      this.cancelRetry?.();
      this.cancelRetry = null;
      this.teardownSocket();
      this.patch(
        this.state.result === null
          ? { phase: 'closed', failure: 'match_over' }
          : { phase: 'ended' },
      );
      return;
    }
    // unauthorised, rate_limited, malformed, pvp_disabled: this client is the
    // problem, and repeating it would only repeat the refusal.
    this.fail(reason, null);
  }

  /**
   * The socket went away without the room saying why.
   *
   * A network blip, a laptop lid, a proxy. The room holds the seat for its
   * grace window, so the honest answer is to mint a NEW capability and come
   * back - and to stop once that window has passed rather than hammering a room
   * that has already given the win away.
   */
  private handleDrop(generation: number): void {
    if (generation !== this.generation) {
      return;
    }
    this.teardownSocket();
    if (this.state.phase === 'ended' || this.state.phase === 'closed') {
      return;
    }
    const matchId = this.state.matchId;
    if (matchId === null) {
      this.fail('reconnect_failed', null);
      return;
    }

    const now = this.deps.now();
    this.droppedAt ??= now;
    const grace = this.state.arena?.reconnectGraceMs ?? 0;
    if (now - this.droppedAt >= grace) {
      /*
       * The window measured from when we actually dropped, on this client's
       * clock, and only as a courtesy: the SERVER's grace window is the one
       * that decides the match. Giving up here simply stops asking.
       */
      this.fail('reconnect_failed', null);
      return;
    }

    this.patch({ phase: 'connecting' });
    const droppedAt = this.droppedAt;
    this.cancelRetry = this.deps.schedule(() => {
      this.cancelRetry = null;
      if (generation !== this.generation) {
        return;
      }
      // A new attempt, and therefore a new capability. The spent one could not
      // reopen anything even if this client kept it.
      this.generation += 1;
      this.droppedAt = droppedAt;
      void this.authoriseAndOpen(this.generation, matchId);
    }, RECONNECT_DELAY_MS);
  }

  /**
   * Writes one frame, and says whether it went.
   *
   * The answer matters for exactly one caller: {@link requestLeave}, which must
   * not let a player believe they abandoned a match the room never heard about.
   * Every other caller ignores it, because a dropped intent is already the
   * documented behaviour there.
   */
  private transmit(frame: unknown): boolean {
    const socket = this.socket;
    if (!socket) {
      return false;
    }
    try {
      socket.send(JSON.stringify(frame));
      return true;
    } catch {
      // A socket that cannot be written to is a socket that is already gone;
      // its close handler does the rest.
      return false;
    }
  }

  private teardownSocket(): void {
    const socket = this.socket;
    this.socket = null;
    if (!socket) {
      return;
    }
    try {
      socket.close();
    } catch {
      /* Already gone. */
    }
  }

  private fail(failure: PvpClientFailure, detail: string | null): void {
    this.generation += 1;
    this.cancelRetry?.();
    this.cancelRetry = null;
    this.teardownSocket();
    this.patch({ phase: 'closed', failure, failureDetail: detail });
  }

  private patch(change: Partial<PvpClientState>): void {
    this.state = { ...this.state, ...change };
    for (const listener of this.listeners) {
      listener(this.state);
    }
  }
}

function reasonOf(error: unknown): string | null {
  const reason = (error as { reason?: unknown } | null)?.reason;
  return typeof reason === 'string' ? reason : null;
}

/* -------------------------------------------------------------------------- */
/*  The browser's socket, wrapped                                             */
/* -------------------------------------------------------------------------- */

/**
 * The real transport.
 *
 * The only part of this module that names `WebSocket`, and it decides nothing -
 * which is what lets every rule above be proven without one.
 */
export function browserPvpSocket(url: string): PvpSocket {
  const socket = new WebSocket(url);
  return {
    send: (data) => {
      socket.send(data);
    },
    close: () => {
      socket.close();
    },
    onOpen: (handler) => {
      socket.addEventListener('open', () => {
        handler();
      });
    },
    onMessage: (handler) => {
      socket.addEventListener('message', (event: MessageEvent) => {
        // Text frames only. The protocol has no binary form, and a client that
        // tried to read one would be reading something nobody sent.
        if (typeof event.data === 'string') {
          handler(event.data);
        }
      });
    },
    onClose: (handler) => {
      socket.addEventListener('close', () => {
        handler();
      });
    },
    onError: (handler) => {
      socket.addEventListener('error', () => {
        handler();
      });
    },
  };
}
